/**
 * `<UploadProgressBar>` — the one progress bar every upload surface
 * draws, so a chat chip, a wiki tree row and a modal cannot disagree on
 * what "60%" looks like or on how it is announced.
 *
 * Two visual states, decided by whether bytes have moved yet:
 *
 *   - **Indeterminate** (`progress == null`). Presign and attach are
 *     sub-second JSON round trips with no byte count, and a registered
 *     transfer reports nothing until its first `onprogress` event. The
 *     sliver travels, and it is `aria-hidden` DECORATION with no role at
 *     all: there is no measurement to report, and asserting `0%` would
 *     claim one — a bar sitting at zero reads as a stall rather than as a
 *     start. The state itself is already carried for assistive tech by
 *     each caller's own "Uploading…" copy and its row's `aria-busy`.
 *   - **Determinate** — a real `progressbar` with `aria-valuenow`, from
 *     the first byte event onward. It carries `tabIndex={-1}`: negative,
 *     so it adds NO tab stop (several in-flight uploads would otherwise
 *     drop several dead stops into a chat composer), but present, because
 *     Biome's `useFocusableInteractive` treats every widget role as a
 *     control and refuses one that can never be focused. Don't drop it.
 *
 * The fill's width is the one legitimate inline style here: it is a
 * computed quantity with no utility-class equivalent, the same exception
 * the project-tint backgrounds take. `inlineSize` rather than `width` so
 * the fill grows from the reading-start edge under `dir="rtl"`.
 */

import type { PutProgress } from './put-with-progress'

export interface UploadProgressBarProps {
  /** Bytes so far, or null while no measurement exists yet. */
  progress: PutProgress | null | undefined
  /**
   * Accessible name. Required rather than defaulted — a page can host
   * several bars at once (a multi-file drop), and "Upload progress" ×4
   * tells a screen-reader user nothing about which file is which.
   */
  label: string
  /** Track classes. Callers own height + spacing; 2px is the house default. */
  className?: string
  /**
   * Track background, when the default would be invisible.
   *
   * A SEPARATE prop rather than something to append to `className`: both
   * would be single background utilities of equal specificity, and with no
   * `tailwind-merge` here the winner is decided by stylesheet order rather
   * than by the caller. Needed on any surface already painted
   * `bg-bg-muted`, where the default track vanishes into its own panel and
   * the fill reads as a stray rule.
   */
  trackBg?: string
}

/**
 * Whole-percent completion, or null when nothing has been measured.
 *
 * Floored, never rounded up: a bar that reads 100% while bytes are still
 * going out is the one reading a user will act on, and acting on it means
 * closing the tab mid-transfer.
 */
export function uploadPercent(progress: PutProgress | null | undefined): number | null {
  if (!progress || progress.total <= 0) return null
  const ratio = progress.loaded / progress.total
  return Math.max(0, Math.min(100, Math.floor(ratio * 100)))
}

export function UploadProgressBar({ progress, label, className, trackBg }: UploadProgressBarProps) {
  const percent = uploadPercent(progress)
  const trackClass =
    `h-0.5 w-full overflow-hidden rounded-full ${trackBg ?? 'bg-bg-muted'} ${className ?? ''}`.trim()

  if (percent === null) {
    return (
      <div aria-hidden="true" className={trackClass}>
        <div className="upload-indeterminate-sliver h-full w-1/3 rounded-full bg-accent" />
      </div>
    )
  }

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      tabIndex={-1}
      className={trackClass}
    >
      <div
        className="h-full rounded-full bg-accent transition-[inline-size] duration-150 ease-out"
        style={{ inlineSize: `${percent}%` }}
      />
    </div>
  )
}
